import { useMemo } from 'react';
import { PostgrestError } from '@supabase/supabase-js';
import { Contact, Note, Initiative, Event, Task, Organization } from '../types/database';
import { InitiativeGoalScore } from '../utils/initiativeGoalScoring';
import { useAuth } from '../contexts/AuthContext';
import { useSupabaseClient } from '@supabase/auth-helpers-react';

interface UseDatabase {
  // Organizations
  getOrganizations: () => Promise<{ data: Organization[] | null; error: PostgrestError | null }>;
  getOrganizationById: (id: string) => Promise<{ data: Organization | null; error: PostgrestError | null }>;
  createOrganization: (organization: Omit<Organization, 'id' | 'created_at' | 'updated_at'>) => 
    Promise<{ data: Organization | null; error: PostgrestError | null }>;
  updateOrganization: (id: string, organization: Partial<Omit<Organization, 'id'>>) =>
    Promise<{ data: Organization | null; error: PostgrestError | null }>;
  deleteOrganization: (id: string) => Promise<{ error: PostgrestError | null }>;
  
  // Contact-Organization relationships
  getContactOrganizations: (contactId: string) => Promise<{ data: Organization[] | null; error: PostgrestError | null }>;
  getAllContactOrganizations: () => Promise<{ data: Array<{ contact_id: string; organization_id: string }> | null; error: PostgrestError | null }>;
  linkContactToOrganization: (contactId: string, organizationId: string) => Promise<{ error: PostgrestError | null }>;
  unlinkContactFromOrganization: (contactId: string, organizationId: string) => Promise<{ error: PostgrestError | null }>;
  getOrganizationContacts: (organizationId: string) => Promise<{ data: Contact[] | null; error: PostgrestError | null }>;

  // Contacts
  getContacts: () => Promise<{ data: Contact[] | null; error: PostgrestError | null }>;
  getContactById: (id: string) => Promise<{ data: Contact | null; error: PostgrestError | null }>;
  getContactsByIds: (ids: string[]) => Promise<{ data: Contact[] | null; error: PostgrestError | null }>;
  getContactByEmail: (email: string) => Promise<{ data: Contact | null; error: PostgrestError | null }>;
  createContact: (contact: Partial<Omit<Contact, 'id' | 'created_at' | 'updated_at' | 'relevance_score'>>) => 
    Promise<{ data: Contact | null; error: PostgrestError | null }>;
  updateContact: (id: string, contact: Partial<Omit<Contact, 'id'>>) =>
    Promise<{ data: Contact | null; error: PostgrestError | null }>;
  deleteContact: (id: string) => Promise<{ error: PostgrestError | null }>;

  // Initiatives
  getInitiatives: () => Promise<{ data: Initiative[] | null; error: PostgrestError | null }>;
  getInitiativeById: (id: string) => Promise<{ data: Initiative | null; error: PostgrestError | null }>;
  createInitiative: (initiative: Omit<Initiative, 'id' | 'created_at' | 'updated_at'>) =>
    Promise<{ data: Initiative | null; error: PostgrestError | null }>;
  updateInitiative: (id: string, initiative: Partial<Omit<Initiative, 'id'>>) =>
    Promise<{ data: Initiative | null; error: PostgrestError | null }>;
  deleteInitiative: (id: string) => Promise<{ error: PostgrestError | null }>;

  // Notes
  getNotes: (referenceId: string) => Promise<{ data: Note[] | null; error: PostgrestError | null }>;
  createNote: (note: Omit<Note, 'id' | 'created_at' | 'updated_at'>) =>
    Promise<{ data: Note | null; error: PostgrestError | null }>;
  updateNote: (id: string, note: Partial<Omit<Note, 'id'>>) =>
    Promise<{ data: Note | null; error: PostgrestError | null }>;
  deleteNote: (id: string) => Promise<{ error: PostgrestError | null }>;

  // Event functions
  getEvents: () => Promise<{ data: Event[] | null; error: PostgrestError | null }>;
  getEventsByContact: (contactId: string) => Promise<{ data: Event[] | null; error: PostgrestError | null }>;
  createEvent: (eventData: Omit<Event, 'id' | 'created_at' | 'updated_at'>) =>
    Promise<{ data: Event | null; error: PostgrestError | null }>;
  updateEvent: (id: string, eventData: Partial<Event>) =>
    Promise<{ data: Event | null; error: PostgrestError | null }>;
  deleteEvent: (id: string) => Promise<{ error: PostgrestError | null }>;
  markEventAsRead: (id: string, action: 'thumbs_up' | 'ignore') => Promise<{ error: PostgrestError | null }>;

  // Task functions
  getTasks: () => Promise<{ data: Task[] | null; error: PostgrestError | null }>;
  getTasksByInitiative: (initiativeId: string) => Promise<{ data: Task[] | null; error: PostgrestError | null }>;
  getTaskById: (id: string) => Promise<{ data: Task | null; error: PostgrestError | null }>;
  createTask: (task: Omit<Task, 'id' | 'created_at' | 'updated_at'>) =>
    Promise<{ data: Task | null; error: PostgrestError | null }>;
  updateTask: (id: string, task: Partial<Omit<Task, 'id'>>) =>
    Promise<{ data: Task | null; error: PostgrestError | null }>;
  deleteTask: (id: string) => Promise<{ error: PostgrestError | null }>;

  // Initiative Goal Scores
  getInitiativeGoalScores: () => Promise<{ data: InitiativeGoalScore[] | null; error: PostgrestError | null }>;
  getInitiativeGoalScoresByInitiative: (initiativeId: string) => Promise<{ data: InitiativeGoalScore[] | null; error: PostgrestError | null }>;
  storeInitiativeGoalScores: (scores: InitiativeGoalScore[]) => Promise<{ error: PostgrestError | null }>;
  deleteInitiativeGoalScores: () => Promise<{ error: PostgrestError | null }>;

  // Networking Suggestions
  getNetworkingSuggestions: (status?: string) => Promise<{ data: any[] | null; error: PostgrestError | null }>;
  acceptNetworkingSuggestion: (suggestionId: string) => Promise<{ error: PostgrestError | null }>;
  rejectNetworkingSuggestion: (suggestionId: string) => Promise<{ error: PostgrestError | null }>;
  ignoreNetworkingSuggestion: (suggestionId: string) => Promise<{ error: PostgrestError | null }>;
  bulkAcceptSuggestions: (suggestionIds: string[]) => Promise<{ error: PostgrestError | null }>;
  recalculateNetworkingSuggestions: () => Promise<{ error: PostgrestError | null }>;

  // PeopleData Labs
  lookupPerson: (contactId: string, email?: string, phone?: string) => Promise<{ data: any | null; error: string | null }>;
  getPeopleByContact: (contactId: string) => Promise<{ data: any[] | null; error: PostgrestError | null }>;
  getPeopleRecord: (contactId: string, lookupType: 'email' | 'phone', lookupValue: string) => Promise<{ data: any | null; error: PostgrestError | null }>;
}

export function useDatabase(): UseDatabase {
  const { user, viewingAsUserId } = useAuth();
  const supabase = useSupabaseClient();

  // Memoize all database functions
  const db = useMemo(() => {
    // Helper to get the effective user ID (for viewing as another user)
    const getEffectiveId = () => {
      return viewingAsUserId || user?.id || null;
    };

    return {
    // Organization methods
    getOrganizations: async () => {
      const effectiveUserId = getEffectiveId();
      if (!effectiveUserId) {
        return { data: null, error: { message: 'User must be authenticated' } as PostgrestError };
      }
      return supabase
        .from('organizations')
        .select('*')
        .eq('user_id', effectiveUserId)
        .order('updated_at', { ascending: false })
        .order('created_at', { ascending: false });
    },

    getOrganizationById: async (id: string) => {
      return supabase
        .from('organizations')
        .select('*')
        .eq('id', id)
        .single();
    },

    createOrganization: async (organization: Omit<Organization, 'id' | 'created_at' | 'updated_at'>) => {
      const effectiveUserId = getEffectiveId();
      if (!effectiveUserId) {
        return { data: null, error: { message: 'User must be authenticated to create organizations' } as PostgrestError };
      }

      try {
        const { data: insertedData, error: insertError } = await supabase
          .from('organizations')
          .insert({
            name: organization.name,
            description: organization.description || undefined,
            website: organization.website || undefined,
            industry: organization.industry || undefined,
            size: organization.size || undefined,
            location: organization.location || undefined,
            user_id: effectiveUserId
          })
          .select('*')
          .single();
        
        if (insertError) {
          console.error('Error creating organization:', insertError);
          return { data: null, error: insertError };
        }

        return { data: insertedData, error: null };
      } catch (err) {
        console.error('Unexpected error creating organization:', err);
        return { data: null, error: err as PostgrestError };
      }
    },

    updateOrganization: async (id: string, organization: Partial<Omit<Organization, 'id'>>) => {
      const effectiveUserId = getEffectiveId();
      if (!effectiveUserId) {
        return { data: null, error: { message: 'User must be authenticated to update organizations' } as PostgrestError };
      }

      const processedOrganization = {
        ...organization,
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('organizations')
        .update(processedOrganization)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating organization:', error);
      }

      return { data, error };
    },

    deleteOrganization: async (id: string) => {
      return supabase
        .from('organizations')
        .delete()
        .eq('id', id);
    },

    // Contact-Organization relationship methods
    getContactOrganizations: async (contactId: string) => {
      const { data, error } = await supabase
        .from('contact_organizations')
        .select(`
          organization_id,
          organizations (*)
        `)
        .eq('contact_id', contactId);
      
      if (error) {
        return { data: null, error };
      }
      
      const organizations = (data || [])
        .map((item: any) => item.organizations)
        .filter((org: any) => org !== null);
      
      return { data: organizations, error: null };
    },

    getAllContactOrganizations: async () => {
      const { data, error } = await supabase
        .from('contact_organizations')
        .select('contact_id, organization_id');
      
      if (error) {
        return { data: null, error };
      }
      
      return { data: data || [], error: null };
    },

    linkContactToOrganization: async (contactId: string, organizationId: string) => {
      const { error } = await supabase
        .from('contact_organizations')
        .insert({
          contact_id: contactId,
          organization_id: organizationId
        });
      
      return { error };
    },

    unlinkContactFromOrganization: async (contactId: string, organizationId: string) => {
      const { error } = await supabase
        .from('contact_organizations')
        .delete()
        .eq('contact_id', contactId)
        .eq('organization_id', organizationId);
      
      return { error };
    },

    getOrganizationContacts: async (organizationId: string) => {
      const { data, error } = await supabase
        .from('contact_organizations')
        .select(`
          contact_id,
          contacts (*)
        `)
        .eq('organization_id', organizationId);
      
      if (error) {
        return { data: null, error };
      }
      
      const contacts = (data || [])
        .map((item: any) => item.contacts)
        .filter((contact: any) => contact !== null);
      
      return { data: contacts, error: null };
    },

    getContacts: async () => {
      const effectiveUserId = getEffectiveId();
      if (!effectiveUserId) {
        return { data: null, error: { message: 'User must be authenticated' } as PostgrestError };
      }
      return supabase
        .from('contacts')
        .select('*')
        .eq('user_id', effectiveUserId)
        .order('updated_at', { ascending: false })
        .order('created_at', { ascending: false });
    },

    getContactById: async (id: string) => {
      return supabase
        .from('contacts')
        .select('*')
        .eq('id', id)
        .single();
    },

    getContactsByIds: async (ids: string[]) => {
      if (!ids || ids.length === 0) {
        return { data: [], error: null };
      }
      const effectiveUserId = getEffectiveId();
      if (!effectiveUserId) {
        return { data: null, error: { message: 'User must be authenticated' } as PostgrestError };
      }
      // Fetch contacts by IDs - RLS will handle permissions
      return supabase
        .from('contacts')
        .select('*')
        .in('id', ids);
    },

    getContactByEmail: async (email: string) => {
      const effectiveUserId = getEffectiveId();
      if (!effectiveUserId) {
        return { data: null, error: { message: 'User must be authenticated to get contacts' } as PostgrestError };
      }

      return supabase
        .from('contacts')
        .select('*')
        .eq('email', email)
        .eq('user_id', effectiveUserId)
        .single();
    },

    createContact: async (contact: Partial<Omit<Contact, 'id' | 'created_at' | 'updated_at' | 'relevance_score'>>) => {
      const effectiveUserId = getEffectiveId();
      if (!effectiveUserId) {
        return { data: null, error: { message: 'User must be authenticated to create contacts' } as PostgrestError };
      }

      try {
        const { data: insertedData, error: insertError } = await supabase
          .from('contacts')
          .insert({
            name: contact.name || '',
            email: contact.email || '',
            phone: contact.phone || undefined,
            description: contact.description || undefined,
            industry: contact.industry || undefined,
            industries: contact.industries || [],
            skills: contact.skills || [],
            interests: contact.interests || [],
            tags: contact.tags || {},
            associated_contacts: contact.associated_contacts || [],
            relationship_types: contact.relationship_types || {},
            is_favorite: contact.is_favorite || false,
            user_id: effectiveUserId
          })
          .select('*')
          .single();
        
        if (insertError) {
          console.error('Error creating contact:', insertError);
          return { data: null, error: insertError };
        }

        const { data: fetchedData, error: fetchError } = await supabase
          .from('contacts')
          .select('*')
          .eq('id', insertedData.id)
          .single();
        
        if (fetchError) {
          console.error('Error fetching created contact:', fetchError);
          return { data: null, error: fetchError };
        }
        
        return { data: fetchedData, error: null };
      } catch (err) {
        console.error('Unexpected error creating contact:', err);
        return { data: null, error: err as PostgrestError };
      }
    },

    updateContact: async (id: string, contact: Partial<Omit<Contact, 'id'>>) => {
      const effectiveUserId = getEffectiveId();
      if (!effectiveUserId) {
        return { data: null, error: { message: 'User must be authenticated to update contacts' } as PostgrestError };
      }

      // Process the contact data for update
      const processedContact = {
        ...contact,
        // Only set default values for fields that are explicitly provided in the update
        ...(contact.industries !== undefined && { industries: contact.industries || [] }),
        ...(contact.skills !== undefined && { skills: contact.skills || [] }),
        ...(contact.interests !== undefined && { interests: contact.interests || [] }),
        ...(contact.tags !== undefined && { tags: contact.tags || {} }),
        ...(contact.associated_contacts !== undefined && { associated_contacts: contact.associated_contacts || [] }),
        ...(contact.relationship_types !== undefined && { relationship_types: contact.relationship_types || {} }),
        // Set updated timestamp
        updated_at: new Date().toISOString()
      };

      console.log('Updating contact with data:', processedContact);

      const { data, error } = await supabase
        .from('contacts')
        .update(processedContact)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating contact:', error);
      }

      return { data, error };
    },

    deleteContact: async (id: string) => {
      return supabase
        .from('contacts')
        .delete()
        .eq('id', id);
    },

    getInitiatives: async () => {
      const effectiveUserId = getEffectiveId();
      if (!effectiveUserId) {
        return { data: null, error: { message: 'User must be authenticated' } as PostgrestError };
      }
      return supabase
        .from('initiatives')
        .select('*')
        .eq('user_id', effectiveUserId)
        .order('updated_at', { ascending: false })
        .order('created_at', { ascending: false });
    },

    getInitiativeById: async (id: string) => {
      const { data, error } = await supabase
        .from('initiatives')
        .select('*')
        .eq('id', id)
        .single();
      return { data, error };
    },

    createInitiative: async (initiative: Omit<Initiative, 'id' | 'created_at' | 'updated_at'>) => {
      const effectiveUserId = getEffectiveId();
      if (!effectiveUserId) {
        console.error('No user ID found in auth context');
        return { data: null, error: { message: 'User must be authenticated to create initiatives' } as PostgrestError };
      }

      try {
        // Get the current session to verify auth state
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        console.log('Current session:', sessionData);
        if (sessionError) {
          console.error('Error getting session:', sessionError);
          return { 
            data: null, 
            error: { 
              message: sessionError.message,
              details: '',
              hint: '',
              code: '42501',
              name: 'PostgrestError'
            } as PostgrestError 
          };
        }

        if (!sessionData.session) {
          console.error('No active session found');
          return { data: null, error: { message: 'No active session' } as PostgrestError };
        }

        const initiativeData = {
          name: initiative.name,
          description: initiative.description || '',
          status: initiative.status || 'Active',
          start_date: initiative.start_date || new Date().toISOString(),
          end_date: initiative.end_date || null,
          contacts: initiative.contacts || [],
          tags: initiative.tags || [],
          notes: initiative.notes || [],
          user_id: sessionData.session.user.id
        };

        console.log('Creating initiative with data:', initiativeData);
        console.log('Session user ID:', sessionData.session.user.id);
        console.log('Auth context user ID:', user?.id);

        const { data: insertedData, error: insertError } = await supabase
          .from('initiatives')
          .insert(initiativeData)
          .select()
          .single();
        
        if (insertError) {
          console.error('Error creating initiative:', insertError);
          return { data: null, error: insertError };
        }

        return { data: insertedData, error: null };
      } catch (err) {
        console.error('Unexpected error creating initiative:', err);
        return { data: null, error: err as PostgrestError };
      }
    },

    updateInitiative: async (id: string, initiative: Partial<Omit<Initiative, 'id'>>) => {
      const { data, error } = await supabase
        .from('initiatives')
        .update(initiative)
        .eq('id', id)
        .select()
        .single();

      // If contacts array was updated, update the updated_at timestamp for all affected contacts
      if (data && initiative.contacts) {
        const oldContacts = data.contacts || [];
        const newContacts = initiative.contacts;
        
        // Find contacts that were added or removed
        const addedContacts = newContacts.filter((contactId: string) => !oldContacts.includes(contactId));
        const removedContacts = oldContacts.filter((contactId: string) => !newContacts.includes(contactId));
        
        // Update timestamps for all affected contacts
        const affectedContacts = [...addedContacts, ...removedContacts];
        if (affectedContacts.length > 0) {
          await Promise.all(affectedContacts.map((contactId: string) => 
            supabase
              .from('contacts')
              .update({ updated_at: new Date().toISOString() })
              .eq('id', contactId)
          ));
        }
      }

      return { data, error };
    },

    deleteInitiative: async (id: string) => {
      const { error } = await supabase
        .from('initiatives')
        .delete()
        .eq('id', id);
      return { error };
    },

    getNotes: async (referenceId: string) => {
      return supabase
        .from('notes')
        .select('*')
        .eq('reference_id', referenceId)
        .order('created_at', { ascending: false });
    },

    createNote: async (note: Omit<Note, 'id' | 'created_at' | 'updated_at'>) => {
      return supabase
        .from('notes')
        .insert(note)
        .select()
        .single();
    },

    updateNote: async (id: string, note: Partial<Omit<Note, 'id'>>) => {
      return supabase
        .from('notes')
        .update(note)
        .eq('id', id)
        .select()
        .single();
    },

    deleteNote: async (id: string) => {
      return supabase
        .from('notes')
        .delete()
        .eq('id', id);
    },

    // Event functions
    getEvents: async () => {
      return await supabase
        .from('events')
        .select('*')
        .order('created_at', { ascending: false });
    },

    getEventsByContact: async (contactId: string) => {
      return await supabase
        .from('events')
        .select('*')
        .eq('contact_id', contactId)
        .order('created_at', { ascending: false });
    },

    createEvent: async (eventData: Omit<Event, 'id' | 'created_at' | 'updated_at'>) => {
      return await supabase
        .from('events')
        .insert(eventData)
        .select()
        .single();
    },

    updateEvent: async (id: string, eventData: Partial<Event>) => {
      return await supabase
        .from('events')
        .update(eventData)
        .eq('id', id)
        .select()
        .single();
    },

    deleteEvent: async (id: string) => {
      return await supabase
        .from('events')
        .delete()
        .eq('id', id);
    },

    markEventAsRead: async (id: string, action: 'thumbs_up' | 'ignore') => {
      return await supabase
        .from('events')
        .update({
          metadata: {
            is_read: true,
            read_action: action
          }
        })
        .eq('id', id);
    },

    // Task functions
    getTasks: async () => {
      const effectiveUserId = getEffectiveId();
      if (!effectiveUserId) {
        return { data: null, error: { message: 'User must be authenticated to get tasks' } as PostgrestError };
      }
      return await supabase
        .from('tasks')
        .select('*')
        .eq('user_id', getEffectiveId())
        .order('due_date', { ascending: true })
        .order('created_at', { ascending: false });
    },

    getTasksByInitiative: async (initiativeId: string) => {
      const effectiveUserId = getEffectiveId();
      if (!effectiveUserId) {
        return { data: null, error: { message: 'User must be authenticated to get tasks' } as PostgrestError };
      }
      return await supabase
        .from('tasks')
        .select('*')
        .eq('user_id', getEffectiveId())
        .eq('entity_type', 'initiative')
        .eq('entity_id', initiativeId)
        .order('due_date', { ascending: true })
        .order('created_at', { ascending: false });
    },

    getTaskById: async (id: string) => {
      const effectiveUserId = getEffectiveId();
      if (!effectiveUserId) {
        return { data: null, error: { message: 'User must be authenticated to get task' } as PostgrestError };
      }
      return await supabase
        .from('tasks')
        .select('*')
        .eq('id', id)
        .eq('user_id', getEffectiveId())
        .single();
    },

    createTask: async (task: Omit<Task, 'id' | 'created_at' | 'updated_at'>) => {
      const effectiveUserId = getEffectiveId();
      if (!effectiveUserId) {
        return { data: null, error: { message: 'User must be authenticated to create tasks' } as PostgrestError };
      }
      return await supabase
        .from('tasks')
        .insert({
          ...task,
            user_id: getEffectiveId()
        })
        .select()
        .single();
    },

    updateTask: async (id: string, task: Partial<Omit<Task, 'id'>>) => {
      const effectiveUserId = getEffectiveId();
      if (!effectiveUserId) {
        return { data: null, error: { message: 'User must be authenticated to update tasks' } as PostgrestError };
      }
      return await supabase
        .from('tasks')
        .update({
          ...task,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .eq('user_id', getEffectiveId())
        .select()
        .single();
    },

    deleteTask: async (id: string) => {
      const effectiveUserId = getEffectiveId();
      if (!effectiveUserId) {
        return { error: { message: 'User must be authenticated to delete tasks' } as PostgrestError };
      }
      return await supabase
        .from('tasks')
        .delete()
        .eq('id', id)
        .eq('user_id', getEffectiveId());
    },

    // Initiative Goal Scores methods
    getInitiativeGoalScores: async () => {
      const effectiveUserId = getEffectiveId();
      if (!effectiveUserId) {
        return { data: null, error: { message: 'User must be authenticated to get initiative scores' } as PostgrestError };
      }
      const { data, error } = await supabase
        .from('initiative_goal_scores')
        .select('*')
        .eq('user_id', getEffectiveId())
        .order('calculated_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching initiative goal scores:', error);
        return { data: null, error };
      }

      // Transform database records to InitiativeGoalScore format
      const scores: InitiativeGoalScore[] = (data || []).map(record => ({
        initiativeId: record.initiative_id,
        goalId: record.goal_id,
        goalCategory: record.goal_category,
        goalTitle: record.goal_title,
        score: record.score,
        reasoning: record.reasoning
      }));

      return { data: scores, error: null };
    },

    getInitiativeGoalScoresByInitiative: async (initiativeId: string) => {
      const effectiveUserId = getEffectiveId();
      if (!effectiveUserId) {
        return { data: null, error: { message: 'User must be authenticated to get initiative scores' } as PostgrestError };
      }
      const { data, error } = await supabase
        .from('initiative_goal_scores')
        .select('*')
        .eq('user_id', getEffectiveId())
        .eq('initiative_id', initiativeId)
        .order('score', { ascending: false });
      
      if (error) {
        console.error('Error fetching initiative goal scores:', error);
        return { data: null, error };
      }

      // Transform database records to InitiativeGoalScore format
      const scores: InitiativeGoalScore[] = (data || []).map(record => ({
        initiativeId: record.initiative_id,
        goalId: record.goal_id,
        goalCategory: record.goal_category,
        goalTitle: record.goal_title,
        score: record.score,
        reasoning: record.reasoning
      }));

      return { data: scores, error: null };
    },

    storeInitiativeGoalScores: async (scores: InitiativeGoalScore[]) => {
      const effectiveUserId = getEffectiveId();
      if (!effectiveUserId) {
        return { error: { message: 'User must be authenticated to store initiative scores' } as PostgrestError };
      }

      try {
        // First, delete existing scores for this user
        const { error: deleteError } = await supabase
          .from('initiative_goal_scores')
          .delete()
          .eq('user_id', getEffectiveId());

        if (deleteError) {
          console.error('Error deleting existing scores:', deleteError);
          return { error: deleteError };
        }

        // Insert new scores
        const scoreRecords = scores.map(score => ({
            user_id: getEffectiveId(),
          initiative_id: score.initiativeId,
          goal_id: score.goalId,
          goal_category: score.goalCategory,
          goal_title: score.goalTitle,
          score: score.score,
          reasoning: score.reasoning,
          calculated_at: new Date().toISOString()
        }));

        const { error: insertError } = await supabase
          .from('initiative_goal_scores')
          .insert(scoreRecords);

        if (insertError) {
          console.error('Error inserting initiative goal scores:', insertError);
          return { error: insertError };
        }

        return { error: null };
      } catch (err) {
        console.error('Unexpected error storing initiative goal scores:', err);
        return { error: err as PostgrestError };
      }
    },

    deleteInitiativeGoalScores: async () => {
      const effectiveUserId = getEffectiveId();
      if (!effectiveUserId) {
        return { error: { message: 'User must be authenticated to delete initiative scores' } as PostgrestError };
      }
      return await supabase
        .from('initiative_goal_scores')
        .delete()
        .eq('user_id', getEffectiveId());
    },

    // Networking Suggestions methods
    getNetworkingSuggestions: async (status?: string) => {
      const effectiveUserId = getEffectiveId();
      if (!effectiveUserId) {
        return { data: null, error: { message: 'User must be authenticated to get networking suggestions' } as PostgrestError };
      }
      
      try {
        // First get suggestions
        let query = supabase
          .from('contact_linking_suggestions')
          .select('*')
          .eq('user_id', getEffectiveId())
          .order('confidence', { ascending: false });
        
        if (status) {
          query = query.eq('status', status);
        }
        
        const { data: suggestions, error: suggestionsError } = await query;
        
        if (suggestionsError) throw suggestionsError;
        if (!suggestions || suggestions.length === 0) {
          return { data: [], error: null };
        }
        
        // Get contact details for each suggestion
        const contactIds = new Set<string>();
        suggestions.forEach(s => {
          contactIds.add(s.contact_a_id);
          contactIds.add(s.contact_b_id);
        });
        
        // Get contacts
        const { data: contacts, error: contactsError } = await supabase
          .from('contacts')
          .select('id, name, email')
          .in('id', Array.from(contactIds));
        
        if (contactsError) throw contactsError;
        
        // Get organization IDs via junction table
        const organizationIds = new Set<string>();
        const contactOrgMap = new Map<string, string[]>(); // contactId -> organizationIds
        
        const contactIdsArray = Array.from(contactIds);
        for (const contactId of contactIdsArray) {
          const { data: contactOrgs } = await db.getContactOrganizations(contactId);
          if (contactOrgs && contactOrgs.length > 0) {
            const orgIds = contactOrgs.map(org => org.id);
            contactOrgMap.set(contactId, orgIds);
            orgIds.forEach(orgId => organizationIds.add(orgId));
          }
        }
        
        // Fetch organizations if any
        let organizationsMap = new Map();
        if (organizationIds.size > 0) {
          const { data: organizations, error: orgError } = await supabase
            .from('organizations')
            .select('id, name')
            .in('id', Array.from(organizationIds));
          
          if (!orgError && organizations) {
            organizationsMap = new Map(organizations.map(org => [org.id, org.name]));
          }
        }
        
        // Map contacts by ID and add company from first organization (or join multiple)
        const contactsMap = new Map((contacts || []).map(c => {
          const orgIds = contactOrgMap.get(c.id) || [];
          const companyNames = orgIds.map(orgId => organizationsMap.get(orgId)).filter(Boolean);
          const company = companyNames.length > 0 ? companyNames.join(', ') : null;
          
          const contactWithCompany = {
            id: c.id,
            name: c.name,
            email: c.email,
            title: null, // Title column doesn't exist in database
            company: company,
          };
          return [c.id, contactWithCompany];
        }));
        
        // Combine suggestions with contact details
        const suggestionsWithContacts = suggestions.map(s => ({
          ...s,
          contact_a: contactsMap.get(s.contact_a_id) || null,
          contact_b: contactsMap.get(s.contact_b_id) || null,
        }));
        
        return { data: suggestionsWithContacts, error: null };
      } catch (err) {
        console.error('Error getting networking suggestions:', err);
        return { data: null, error: err as PostgrestError };
      }
    },

    acceptNetworkingSuggestion: async (suggestionId: string) => {
      const effectiveUserId = getEffectiveId();
      if (!effectiveUserId) {
        return { error: { message: 'User must be authenticated to accept suggestions' } as PostgrestError };
      }

      try {
        // Get the suggestion
        const { data: suggestion, error: suggestionError } = await supabase
          .from('contact_linking_suggestions')
          .select('*')
          .eq('id', suggestionId)
          .eq('user_id', getEffectiveId())
          .single();

        if (suggestionError) throw suggestionError;
        if (!suggestion) throw new Error('Suggestion not found');

        // Get both contacts
        const { data: contactA, error: contactAError } = await supabase
          .from('contacts')
          .select('*')
          .eq('id', suggestion.contact_a_id)
          .single();

        if (contactAError) throw contactAError;

        const { data: contactB, error: contactBError } = await supabase
          .from('contacts')
          .select('*')
          .eq('id', suggestion.contact_b_id)
          .single();

        if (contactBError) throw contactBError;

        // Create bidirectional relationship
        const updatedAssociatedContactsA = [...(contactA.associated_contacts || []), contactB.id];
        const updatedRelationshipTypesA = {
          ...(contactA.relationship_types || {}),
          [contactB.id]: suggestion.relationship_type || 'Associate',
        };

        const updatedAssociatedContactsB = [...(contactB.associated_contacts || []), contactA.id];
        const updatedRelationshipTypesB = {
          ...(contactB.relationship_types || {}),
          [contactA.id]: suggestion.relationship_type || 'Associate',
        };

        // Update both contacts
        const { error: updateAError } = await supabase
          .from('contacts')
          .update({
            associated_contacts: updatedAssociatedContactsA,
            relationship_types: updatedRelationshipTypesA,
            updated_at: new Date().toISOString()
          })
          .eq('id', contactA.id);

        if (updateAError) throw updateAError;

        const { error: updateBError } = await supabase
          .from('contacts')
          .update({
            associated_contacts: updatedAssociatedContactsB,
            relationship_types: updatedRelationshipTypesB,
            updated_at: new Date().toISOString()
          })
          .eq('id', contactB.id);

        if (updateBError) throw updateBError;

        // Update suggestion status
        const { error: updateSuggestionError } = await supabase
          .from('contact_linking_suggestions')
          .update({
            status: 'accepted',
            updated_at: new Date().toISOString()
          })
          .eq('id', suggestionId);

        if (updateSuggestionError) throw updateSuggestionError;

        return { error: null };
      } catch (err) {
        console.error('Error accepting networking suggestion:', err);
        return { error: err as PostgrestError };
      }
    },

    rejectNetworkingSuggestion: async (suggestionId: string) => {
      const effectiveUserId = getEffectiveId();
      if (!effectiveUserId) {
        return { error: { message: 'User must be authenticated to reject suggestions' } as PostgrestError };
      }

      return await supabase
        .from('contact_linking_suggestions')
        .update({
          status: 'rejected',
          updated_at: new Date().toISOString()
        })
        .eq('id', suggestionId)
        .eq('user_id', getEffectiveId());
    },

    ignoreNetworkingSuggestion: async (suggestionId: string) => {
      const effectiveUserId = getEffectiveId();
      if (!effectiveUserId) {
        return { error: { message: 'User must be authenticated to ignore suggestions' } as PostgrestError };
      }

      return await supabase
        .from('contact_linking_suggestions')
        .update({
          status: 'ignored',
          updated_at: new Date().toISOString()
        })
        .eq('id', suggestionId)
        .eq('user_id', getEffectiveId());
    },

    bulkAcceptSuggestions: async (suggestionIds: string[]) => {
      const effectiveUserId = getEffectiveId();
      if (!effectiveUserId) {
        return { error: { message: 'User must be authenticated to bulk accept suggestions' } as PostgrestError };
      }

      try {
        // Process each suggestion
        for (const suggestionId of suggestionIds) {
          // Get the suggestion
          const { data: suggestion, error: suggestionError } = await supabase
            .from('contact_linking_suggestions')
            .select('*')
            .eq('id', suggestionId)
            .eq('user_id', getEffectiveId())
            .single();

          if (suggestionError || !suggestion) {
            console.error(`Error fetching suggestion ${suggestionId}:`, suggestionError);
            continue;
          }

          // Get both contacts
          const { data: contactA, error: contactAError } = await supabase
            .from('contacts')
            .select('*')
            .eq('id', suggestion.contact_a_id)
            .single();

          if (contactAError || !contactA) {
            console.error(`Error fetching contact A for suggestion ${suggestionId}:`, contactAError);
            continue;
          }

          const { data: contactB, error: contactBError } = await supabase
            .from('contacts')
            .select('*')
            .eq('id', suggestion.contact_b_id)
            .single();

          if (contactBError || !contactB) {
            console.error(`Error fetching contact B for suggestion ${suggestionId}:`, contactBError);
            continue;
          }

          // Create bidirectional relationship
          const updatedAssociatedContactsA = [...(contactA.associated_contacts || []), contactB.id];
          const updatedRelationshipTypesA = {
            ...(contactA.relationship_types || {}),
            [contactB.id]: suggestion.relationship_type || 'Associate',
          };

          const updatedAssociatedContactsB = [...(contactB.associated_contacts || []), contactA.id];
          const updatedRelationshipTypesB = {
            ...(contactB.relationship_types || {}),
            [contactA.id]: suggestion.relationship_type || 'Associate',
          };

          // Update both contacts
          const { error: updateAError } = await supabase
            .from('contacts')
            .update({
              associated_contacts: updatedAssociatedContactsA,
              relationship_types: updatedRelationshipTypesA,
              updated_at: new Date().toISOString()
            })
            .eq('id', contactA.id);

          if (updateAError) {
            console.error(`Error updating contact A for suggestion ${suggestionId}:`, updateAError);
            continue;
          }

          const { error: updateBError } = await supabase
            .from('contacts')
            .update({
              associated_contacts: updatedAssociatedContactsB,
              relationship_types: updatedRelationshipTypesB,
              updated_at: new Date().toISOString()
            })
            .eq('id', contactB.id);

          if (updateBError) {
            console.error(`Error updating contact B for suggestion ${suggestionId}:`, updateBError);
            continue;
          }

          // Update suggestion status
          await supabase
            .from('contact_linking_suggestions')
            .update({
              status: 'accepted',
              updated_at: new Date().toISOString()
            })
            .eq('id', suggestionId);
        }

        return { error: null };
      } catch (err) {
        console.error('Error bulk accepting suggestions:', err);
        return { error: err as PostgrestError };
      }
    },

    recalculateNetworkingSuggestions: async () => {
      const effectiveUserId = getEffectiveId();
      if (!effectiveUserId) {
        return { error: { message: 'User must be authenticated to recalculate suggestions' } as PostgrestError };
      }

      try {
        // Call the networking suggestions API
        const response = await fetch('/.netlify/functions/networking-suggestions', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId: getEffectiveId(),
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to recalculate suggestions: ${response.status} ${errorText}`);
        }

        const result = await response.json();
        if (result.error) {
          throw new Error(result.error);
        }

        return { error: null };
      } catch (err) {
        console.error('Error recalculating networking suggestions:', err);
        return { error: err as PostgrestError };
      }
    },

    // PeopleData Labs functions
    lookupPerson: async (contactId: string, email?: string, phone?: string) => {
      try {
        const response = await fetch('/.netlify/functions/peopledata-lookup', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            contactId,
            email,
            phone,
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          let errorData;
          try {
            errorData = JSON.parse(errorText);
          } catch (e) {
            throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
          }
          throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        console.log('lookupPerson result:', result);
        
        if (result.error) {
          return { data: null, error: result.error };
        }

        if (!result.success) {
          return { data: null, error: 'Lookup failed' };
        }

        return { data: result.data, error: null };
      } catch (err) {
        console.error('Error looking up person:', err);
        return { data: null, error: err instanceof Error ? err.message : 'Unknown error' };
      }
    },

    getPeopleByContact: async (contactId: string) => {
      const effectiveUserId = getEffectiveId();
      if (!effectiveUserId) {
        return { data: null, error: { message: 'User must be authenticated' } as PostgrestError };
      }
      return supabase
        .from('people')
        .select('*')
        .eq('contact_id', contactId)
        .eq('user_id', effectiveUserId)
        .order('created_at', { ascending: false });
    },

    getPeopleRecord: async (contactId: string, lookupType: 'email' | 'phone' | 'name', lookupValue: string) => {
      const effectiveUserId = getEffectiveId();
      if (!effectiveUserId) {
        return { data: null, error: { message: 'User must be authenticated' } as PostgrestError };
      }
      return supabase
        .from('people')
        .select('*')
        .eq('contact_id', contactId)
        .eq('lookup_type', lookupType)
        .eq('lookup_value', lookupValue)
        .eq('user_id', effectiveUserId)
        .single();
    },
    };
  }, [user, supabase, viewingAsUserId]); // Depend on user, supabase, and viewingAsUserId for viewing as functionality

  return db;
}

/**
 * Sends a user query and the current database context to ChatGPT via Netlify function.
 * @param userQuery The user's question or prompt.
 * @param contacts Array of contacts to provide context.
 * @param initiatives Array of initiatives to provide context.
 * @param events Array of events to provide context.
 * @returns The response from ChatGPT as a string.
 */
export async function askChatGPT({
  userQuery,
  contacts,
  initiatives,
  events,
  tasks,
  instructions = 'You are a helpful assistant that answers questions about contacts, initiatives, events, and tasks.'
}: {
  userQuery: string;
  contacts: any[];
  initiatives: any[];
  events: any[];
  tasks?: any[];
  instructions?: string;
}): Promise<string> {
  try {
    console.log('Calling Netlify function with:', { userQuery, contactsCount: contacts.length, initiativesCount: initiatives.length, eventsCount: events.length, tasksCount: tasks?.length || 0 });
    
    const response = await fetch('/.netlify/functions/ask-chatgpt', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userQuery,
        contacts,
        initiatives,
        events,
        tasks,
        instructions,
      }),
    });

    console.log('Response status:', response.status);
    console.log('Response headers:', Object.fromEntries(response.headers.entries()));

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error response text:', errorText);
      
      // Handle specific HTTP status codes
      if (response.status === 502) {
        throw new Error('Netlify function is not available. Please check your deployment and environment variables.');
      }
      
      if (response.status === 503) {
        throw new Error('OpenAI service is temporarily unavailable. Please try again in a few moments.');
      }
      
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch (e) {
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const responseText = await response.text();
    console.log('Response text:', responseText);
    
    let data;
    try {
      data = JSON.parse(responseText);
    } catch (e) {
      console.error('Failed to parse JSON:', responseText);
      throw new Error('Invalid JSON response from server');
    }
    
    return data.result;
  } catch (error) {
    console.error('Error calling ChatGPT function:', error);
    throw error;
  }
} 